<template>
  <div v-if="schemaLoaded && schemaLoaded.data">
    <popup2 v-model="showPopup" width="428" @click:outside="handleClose">
      <template v-if="typeAction === 'edit-rights'">
        <v-card>
          <v-card-title class="primary--text pa-4">
            Edit rights
          </v-card-title>
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"
            v-model="popupValue"
            item-value="id"
            item-text="name"
            :items="getRights()"
            return-object
            class="px-4"
          ></v-select>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="handleClose">Cancel</v-btn>
            <v-btn color="primary" text @click="changeRights">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </template>
      <action-schema-control
        :loading="popupLoading"
        @cancel="handleClose"
        :schema-id="schemaLoaded.data.schema.id"
        @action-control="routeAction"
        :edited-control="currentContext"
        :type-view="typeAction"
        v-else-if="
          typeAction === 'add-schema-control' ||
            typeAction === 'edit-control-scheme'
        "
      ></action-schema-control>

      <action-schema-property
        :loading="popupLoading"
        @cancel="handleClose"
        :schema-id="schemaLoaded.data.schema.id"
        @action-control="routeAction"
        :prefilled-group="currentContext"
        :edited-control="isObject(currentContext) ? currentContext : {}"
        :type-view="typeAction"
        :types="propertyTypes"
        v-else-if="
          typeAction === 'add-schema-property' ||
            typeAction === 'edit-schema-property'
        "
        :autocomplete-items="groupPropertieSelect"
      >
      </action-schema-property>

      <edit-scheme
        v-else-if="typeAction === 'edit-scheme'"
        @close="handleClose"
        :entity="schemaLoaded.data.schema"
      />
      <default-confirmation
        v-else-if="typeAction === 'delete'"
        type-entity="schema"
        :loading="popupLoading"
        @close="handleClose"
        @delete="deleteSchema"
      />
      <default-confirmation
        v-else-if="typeAction === 'delete-schema-property'"
        type-entity="schema property"
        text-delete="Delete this property completely?"
        :loading="popupLoading"
        @close="handleClose"
        @delete="deleteSchemaProperty"
      />
      <edit-rights-group
        v-else-if="typeAction === 'change-group-access'"
        :entity="schemaLoaded.data.schema"
        :loading="popupLoading"
        @save="changeGroupRights"
        @close="handleClose"
      />
      <duplicate-schema
        v-else-if="typeAction === 'duplicate-schema'"
        :entity="schemaLoaded.data.schema"
        @close="handleClose"
      />
      <apply-default-value
        v-else-if="typeAction === 'apply-default-value'"
        :entity="schemaLoaded.data.schema"
        :context="currentContext"
        @close="handleClose"
      />
      <layout-popup v-if="typeAction === 'show-info-schema-property'">
        <template #header>
          {{
            currentContext.description ||
              currentContext.longName ||
              currentContext.property
          }}
        </template>
        <template #default>
          <div class="pb-4">
            <div class="px-5 " v-for="(item, key) of currentContext" :key="key">
              <div class="pt-3">
                {{ transformKey(key, item, currentContext.typeId).key }}:
                {{
                  transformKey(key, item, currentContext.typeId).value || 'n/a'
                }}
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <v-spacer></v-spacer>
          <v-btn text @click="handleClose">close</v-btn>
          <v-btn
            color="primary"
            @click="copyToClipboard(JSON.stringify(currentContext))"
            text
            >copy</v-btn
          >
        </template>
      </layout-popup>
    </popup2>

    <app-entity
      :current-show="currentShow"
      :entity="entity"
      :nested-entity="nestedEntity"
      :is-nested-entity="isNestedEntity"
      :object="schemaLoaded.data.schema"
      :current-context="currentContext"
      @show-nested-entity="showNestedEntity"
      :loading="loading"
      :tab-data="logsList"
      @close-card="closeCard"
      @action="handleAction"
      @close-history="currentShow = 'entity'"
      @close-nested-card="closeNestedCard"
      @show-full-logs="showFullLogs"
    />
  </div>
</template>

<script>
import startCase from 'lodash.startcase';

import { onMounted, ref } from '@vue/composition-api';
import useScheme from '@/api/schemas/useScheme';
import useSchema from '@/api/schemas';

import ActionSchemaControl from '@/components/app-entity/modals/ActionSchemaControl';
import ActionSchemaProperty from '@/components/app-entity/modals/ActionSchemaProperty';
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import LayoutPopup from '@/layouts/LayoutPopup';
import EditScheme from '@/router/routes/schemas/edit';

import copyToClipboard from '@/utils/copyToClipboard';
import convertDate from '@/utils/convertDate';
import isObject from '@/utils/isObject';
import DuplicateSchema from '@/components/app-entity/modals/DuplicateSchema';
import { useMutation } from '@vue/apollo-composable';
import gql from '@/api/schemas/item.graphql';
import { isJson } from '@/utils/getCurrentType';
import { Wormhole } from 'portal-vue';
import ApplyDefaultValue from '@/components/app-entity/modals/ApplyDefaultValue.vue';

export default {
  name: 'SchemasItem',
  components: {
    ApplyDefaultValue,
    DuplicateSchema,
    EditScheme,
    ActionSchemaProperty,
    ActionSchemaControl,
    DefaultConfirmation,
    LayoutPopup,
    EditRightsGroup: () =>
      import('@/components/app-entity/modals/EditRightsGroup')
  },
  props: ['id', 'isNestedEntity'],
  setup(props, context) {
    const entityComposition = useScheme(props, context);
    const { schema, schemas } = useSchema();
    const copyValue = ref('');

    const closeNestedCard = () => {
      Wormhole.close({
        to: 'nested',
        from: 'object'
      });

      Wormhole.close({
        to: 'user-portal',
        from: 'user'
      });
    };

    const closeCard = () => {
      closeNestedCard();
      entityComposition.entity.value = null;
      context.root.$router.push({
        name: context.root.$route.meta.rootRouteName,
        query: context.root.$route.query
      });
    };

    const routeAction = e => {
      if (e.type === 'add-schema-control') {
        createSchemaControl(e.formData);
      } else if (e.type === 'edit-control-scheme') {
        editSchemaControl({ ...e.formData, id: e.id });
      } else if (e.type === 'add-schema-property') {
        createSchemaProperty(e.formData);
      } else if (e.type === 'edit-schema-property') {
        editSchemaProperty({ ...e.formData, id: e.id });
      }
    };

    const { mutate: createSchemaControlMutation } = useMutation(
      gql.createSchemaControl
    );

    const { mutate: editSchemaControlMutation } = useMutation(
      gql.editSchemaControl
    );

    const createSchemaControl = payload => {
      entityComposition.popupLoading.value = true;
      createSchemaControlMutation({
        input: {
          schemaControl: {
            ...payload
          }
        }
      })
        .then(() => {
          entityComposition.handleClose();
        })
        .finally(() => {
          entityComposition.popupLoading.value = false;
          schema.loading.value = false;
        });
    };

    const createSchemaProperty = payload => {
      entityComposition.popupLoading.value = true;
      schema
        .addProperty({
          ...payload
        })
        .then(() => {
          entityComposition.handleClose();
        })
        .finally(() => {
          entityComposition.popupLoading.value = false;
          schema.loading.value = false;
        });
    };

    const editSchemaProperty = payload => {
      entityComposition.popupLoading.value = true;
      schema
        .updateProperty(payload.id, {
          ...payload
        })
        .then(() => {
          entityComposition.handleClose();
        })
        .finally(() => {
          entityComposition.popupLoading.value = false;
          schema.loading.value = false;
        });
    };

    const updateTable = isCloseCard => {
      if (isCloseCard) context.root.$router.push({ name: 'schemas' });
      return schemas.load();
    };

    const deleteSchema = () => {
      entityComposition.popupLoading.value = true;
      schemas
        .remove([{ id: entityComposition.entityId.value }])
        .then(() => {
          entityComposition.entity.value = null;
          return updateTable(true);
        })
        .then(() => {
          entityComposition.handleClose();
        })
        .finally(() => {
          entityComposition.popupLoading.value = false;
        });
    };

    const deleteSchemaProperty = () => {
      entityComposition.popupLoading.value = true;
      schema
        .deleteSchemaProperty(entityComposition.currentContext.value.raw.id)
        .then(() => {
          // entityComposition.loadData(context.root.$route.params.id);
          entityComposition.handleClose();
        })
        .finally(() => {
          entityComposition.popupLoading.value = false;
          schema.loading.value = false;
        });
    };

    const editSchemaControl = payload => {
      entityComposition.popupLoading.value = true;
      editSchemaControlMutation(
        {
          input: {
            id: payload.id,
            patch: {
              argument: payload.argument,
              description: payload.description,
              rpc: payload.rpc,
              typeSpec: payload.typeSpec
            }
          }
        },
        {
          fetchPolicy: 'no-cache'
        }
      ).then(() => {
        entityComposition.popupLoading.value = false;
        entityComposition.handleClose();
      });
    };

    const transformKey = (key, value, typeId) => {
      let str = startCase(key).toLowerCase();
      let result = {
        key: str.charAt(0).toUpperCase() + str.slice(1),
        value: typeof value === 'object' ? JSON.stringify(value) : `${value}`
      };

      if (key === 'createdAt' || key === 'updatedAt') {
        result.value = convertDate(value);
      }
      if (value === null) {
        result.value = 'n/a';
      }
      return result;
    };

    onMounted(() => {
      entityComposition.loadSchema();
      entityComposition.subscribeToUpdate();
    });
    return {
      ...entityComposition,
      copyValue,
      closeCard,
      deleteSchema,
      createSchemaControl,
      editSchemaControl,
      routeAction,
      deleteSchemaProperty,
      transformKey,
      copyToClipboard,
      isObject
    };
  }
};
</script>
